// /en/tax/dta/$country — EN DTA detail. Phase P-TAX-DEEP-DIVE.
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getDTA, DTA_COUNTRIES } from "@/data/tax-dta";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Globe2, ChevronRight, Sparkles, FileText, ShieldCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/tax/dta/$country")({
  loader: ({ params }) => {
    const dta = getDTA(params.country);
    if (!dta) throw notFound();
    return { dta };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const d = loaderData.dta;
    const url = `${SITE}/en/tax/dta/${d.slug}`;
    const title = `Thailand-${d.countryEn} DTA ${d.effective} — WHT Rates, Foreign Tax Credit | NYC`;
    const desc = `Double Tax Agreement Thailand-${d.countryEn} (effective ${d.effective}) — Dividend ${d.whtDividend}, Interest ${d.whtInterest}, Royalty ${d.whtRoyalty}. Credit method with documents and examples. Talk to a specialist today.`;
    const faqs = [
      { q: `What is the dividend WHT rate under Thailand-${d.countryEn} DTA?`, a: d.whtDividend },
      { q: `Which Foreign Tax Credit method applies?`, a: d.method === "credit" ? "Credit method" : d.method === "exemption" ? "Exemption method" : "Mixed method" },
      { q: `When did this DTA come into effect?`, a: `Signed ${d.signed}, effective ${d.effective}` },
      { q: `Who benefits from this DTA?`, a: `${d.useCaseEn} and anyone with income from ${d.countryEn}` },
    ];
    return {
      meta: [
        { title: ctaTitle(title, "/en/tax/dta/$country", "en") }, { name: "description", content: ctaDesc(desc, "/en/tax/dta/$country", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/tax/dta/$country", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/tax/dta/$country", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/tax/dta/${d.slug}`, `/en/tax/dta/${d.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", ".quick-answer", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Article",
              headline: title, description: desc, inLanguage: "en",
              author: { "@type": "Organization", name: "NYC Legal & Notary Service", url: SITE },
              publisher: { "@type": "Organization", name: "NYC Legal & Notary Service", url: SITE, logo: { "@type": "ImageObject", url: `${SITE}/logo.png` } },
              datePublished: "2026-06-12", dateModified: "2026-06-12",
              mainEntityOfPage: url,
            }),
          },
          faqPageSchema(faqs),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Thai Tax", item: `${SITE}/en/tax` },
                { "@type": "ListItem", position: 3, name: `Thailand-${d.countryEn} DTA`, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-16 text-center">
      <h1 className="text-3xl font-extrabold">DTA country not found</h1>
      <Link to="/en/tax" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-bold text-primary-foreground">Back to Tax Hub</Link>
    </main>
  ),
  errorComponent: () => <main className="p-16 text-center">Error</main>,
  component: DTAPageEn,
});

function DTAPageEn() {
  const { dta: d } = Route.useLoaderData();
  const quick = `The Double Tax Agreement between Thailand and ${d.countryEn} (signed ${d.signed}, effective ${d.effective}) uses the ${d.method} method. It caps WHT at Dividend ${d.whtDividend}, Interest ${d.whtInterest}, and Royalty ${d.whtRoyalty} to eliminate double taxation for taxpayers earning in both countries.`;
  const others = DTA_COUNTRIES.filter((x) => x.slug !== d.slug).slice(0, 12);
  return (
    <main className="min-h-screen bg-background text-foreground">
      <nav className="mx-auto max-w-5xl px-4 pt-6 text-xs text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/en/tax" className="hover:underline">Thai Tax</Link>
        <ChevronRight className="mx-1 inline h-3 w-3" />
        <span>Thailand-{d.countryEn} DTA</span>
      </nav>

      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-8 pb-12">
        <div className="mx-auto max-w-5xl">
          <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary">
            <Globe2 className="h-3 w-3" /> Double Tax Agreement
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">Thailand-{d.countryEn} DTA ({d.iso2})</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">{d.notesEn} — relevant for {d.useCaseEn}.</p>

          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">What does the Thailand-{d.countryEn} DTA cover?</h2>
            <p className="direct-answer mt-2 text-sm leading-relaxed sm:text-base">{quick}</p>
          </div>

          <dl className="mt-6 grid max-w-3xl grid-cols-1 gap-0 overflow-hidden rounded-2xl border border-border bg-card text-sm sm:grid-cols-2" data-speakable="true">
            {[
              { k: "Treaty Partner", v: `${d.countryEn} (${d.iso2})` },
              { k: "Signed", v: d.signed },
              { k: "Effective", v: d.effective },
              { k: "Dividend WHT", v: d.whtDividend },
              { k: "Interest WHT", v: d.whtInterest },
              { k: "Royalty WHT", v: d.whtRoyalty },
              { k: "Method", v: d.method === "credit" ? "Credit method" : d.method === "exemption" ? "Exemption method" : "Mixed" },
              { k: "Primary Use Case", v: d.useCaseEn },
            ].map((r, i, arr) => (
              <div key={r.k} className={`flex justify-between gap-3 px-4 py-2.5 ${i < arr.length - 1 ? "border-b border-border" : ""}`}>
                <dt className="font-medium text-muted-foreground">{r.k}</dt>
                <dd className="text-right font-semibold">{r.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-5xl grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 text-xl font-bold"><ShieldCheck className="h-5 w-5 text-primary" /> How to Claim DTA Benefits</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
              <li>Request a Thai Certificate of Residence (form RO22)</li>
              <li>Submit it to the payer in {d.countryEn} to reduce WHT at source</li>
              <li>Keep the foreign Tax Payment Certificate</li>
              <li>Get the certificate certified (NAATI/MFA/lawyer)</li>
              <li>File PND.90 claiming the Foreign Tax Credit</li>
            </ol>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6">
            <h2 className="flex items-center gap-2 text-xl font-bold"><FileText className="h-5 w-5 text-primary" /> Documents Required</h2>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-muted-foreground">
              <li>Tax Payment Certificate from {d.countryEn}</li>
              <li>Certified Thai translation (NAATI/MFA/lawyer)</li>
              <li>Thai Certificate of Residence (RO22)</li>
              <li>Bank statements as proof of remittance</li>
              <li>{d.countryEn} tax return for the relevant year</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-12">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-bold">Other Thai DTAs to Explore</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {others.map((x) => (
              <Link key={x.slug} to="/en/tax/dta/$country" params={{ country: x.slug }} className="rounded-lg border border-border bg-card p-3 text-sm hover:border-primary">
                <span className="font-bold">{x.countryEn}</span>
                <div className="mt-1 text-[11px] text-muted-foreground">Div {x.whtDividend}</div>
              </Link>
            ))}
          </div>
          <Link to="/en/tax" className="mt-8 inline-block text-sm text-primary hover:underline">← Back to Tax Hub</Link>
        </div>
      </section>

      <TrustLayer />
    </main>
  );
}
